import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import WeKitLogo from "@/components/WeKitLogo";
import PageHead from "@/components/PageHead";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const PRO_FEATURES = [
  "30-day AI Growth Plan",
  "Skill roadmap for your chosen career",
  "Internships & competitions feed",
  "AI Resume builder",
  "YOMA AI career coach (chat)",
  "AI Mock interview practice",
  "Goal nudges & daily micro-challenges",
  "Voice journal with weekly AI insights",
  "AI-drafted LinkedIn posts",
];

export default function ProWaitlist() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [interest, setInterest] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const cleanEmail = email.trim().toLowerCase();
      const cleanName = name.trim() || null;
      const cleanInterest = interest.trim() || null;
      const { error } = await supabase.functions.invoke("pro-waitlist-join", {
        body: {
          email: cleanEmail,
          name: cleanName,
          interest: cleanInterest,
        },
      });
      if (error) throw error;
      setDone(true);
      toast.success("You're on the list. We'll be in touch.");
    } catch (err: any) {
      toast.error(err.message ?? "Could not join waitlist");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageHead
        title="Join the WeKIT™ Pro Waitlist | Career Clarity 360"
        description="Be first in line for WeKIT™ Pro — AI coaching, a personalised growth plan, mock interviews, resume builder and an opportunities feed."
        path="/pro-waitlist"
      />

      <header className="border-b">
        <div className="container max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
          <WeKitLogo className="h-7" />
          <div className="w-16" />
        </div>
      </header>

      <main className="container max-w-5xl mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-10">
          <Badge className="rounded-full bg-primary/15 text-primary border-primary/30 gap-1.5 px-3 py-1.5 mb-4">
            <Sparkles className="h-3.5 w-3.5" />
            WeKIT™ Pro — Coming soon
          </Badge>
          <h1 className="font-display text-3xl sm:text-5xl font-bold mb-3">
            Be first in line for WeKIT™ Pro
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Pro unlocks AI coaching, your personalised growth plan, mock interviews,
            resume builder, opportunities feed and more. Join the waitlist for
            launch-day pricing and early access.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 items-start">
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <h3 className="font-semibold mb-4">What's coming in Pro</h3>
              <ul className="space-y-2.5">
                {PRO_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardContent className="p-6">
              {done ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-primary mx-auto mb-3" />
                  <h3 className="font-display text-xl font-bold mb-1">You're on the list</h3>
                  <p className="text-sm text-muted-foreground mb-5">
                    We'll email you the moment Pro is ready.
                  </p>
                  <Button asChild variant="outline" className="rounded-full">
                    <Link to="/dashboard">Back to dashboard</Link>
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <h3 className="font-semibold">Join the waitlist</h3>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="name">Name (optional)</Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="interest">Which feature excites you most? (optional)</Label>
                    <Input
                      id="interest"
                      value={interest}
                      onChange={(e) => setInterest(e.target.value)}
                      placeholder="e.g. Mock interviews"
                    />
                  </div>
                  <Button type="submit" disabled={loading || !email} className="w-full rounded-full">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Join waitlist"}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    No spam. One email when Pro launches.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
